import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { installGuards } from '@/app/router/guards'

/**
 * Application routes (UI/UX Specification §3 screen inventory).
 *
 * Route modules are lazy-loaded so each feature is a separate chunk (SDD §10.2).
 * Navigation guards are a usability aid only. They are NEVER an authorization
 * control - the backend re-checks permission and scope on every request
 * (ARCHITECTURE_GUARDRAILS.md §5.4).
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** Must be signed in. */
    auth?: boolean
    /** Only sensible while signed out (login, reset, invitation). */
    guestOnly?: boolean
    /** Needs a selected tenant (tenant namespace). */
    tenant?: boolean
    /** Needs at least one platform permission. */
    platform?: boolean
    /** Reachable while a security step (password change / MFA enrolment) is pending. */
    allowPendingSecurity?: boolean
    /** Any of these effective tenant permissions opens the route; none → access denied. */
    permissions?: string[]
    /** ...and every one of these, which the page needs to load (e.g. its settings read). */
    requires?: string[]
    /** BRD-089: a team's designated lead opens the route too, whatever their role. */
    teamLead?: boolean
    /** Any of these platform permissions opens the route; none → access denied. */
    platformPermissions?: string[]
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      // The bare address: this layout owns '/', so the redirect lives here - signed out
      // it ends on sign-in, signed in on the workspace (guards: landingFor)
      { path: '', name: 'root', redirect: { name: 'select-tenant' } },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/features/auth/views/LoginView.vue'),
        meta: { guestOnly: true, title: 'Sign in' },
      },
      {
        path: 'no-workspace',
        name: 'no-workspace',
        component: () => import('@/features/auth/views/NoWorkspaceView.vue'),
        meta: { title: 'No workspace' },
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/features/auth/views/ForgotPasswordView.vue'),
        meta: { guestOnly: true, title: 'Reset your password' },
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/features/auth/views/ResetPasswordView.vue'),
        meta: { guestOnly: true, title: 'Choose a new password' },
      },
      {
        path: 'invite/accept',
        name: 'invite-accept',
        component: () => import('@/features/auth/views/InviteAcceptView.vue'),
        meta: { guestOnly: true, title: 'Accept your invitation' },
      },
      {
        path: 'security/setup',
        name: 'security-setup',
        component: () => import('@/features/auth/views/SecuritySetupView.vue'),
        meta: { auth: true, allowPendingSecurity: true, title: 'Secure your account' },
      },
      {
        path: 'select-tenant',
        name: 'select-tenant',
        component: () => import('@/features/auth/views/SelectTenantView.vue'),
        meta: { auth: true, title: 'Choose a workspace' },
      },
    ],
  },
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { auth: true, tenant: true },
    children: [
      {
        path: '',
        name: 'tenant-dashboard',
        component: () => import('@/features/dashboard/views/TenantDashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'organization',
        name: 'organization',
        component: () => import('@/features/organization/views/OrganizationView.vue'),
        meta: {
          title: 'Organisation',
          permissions: [
            'tenant.settings.manage',
            'organization.branch.view',
            'organization.department.view',
            'organization.team.view',
            'organization.region.view',
          ],
        },
      },
      {
        path: 'employees',
        name: 'employees',
        component: () => import('@/features/employees/views/EmployeesView.vue'),
        meta: { title: 'Employees', permissions: ['employee.view'] },
      },
      {
        path: 'attendance',
        name: 'attendance',
        component: () => import('@/features/attendance/views/MyAttendanceView.vue'),
        meta: { title: 'My attendance', permissions: ['attendance.checkin'] },
      },
      {
        path: 'attendance/team',
        name: 'attendance-team',
        component: () => import('@/features/attendance/views/TeamSignInView.vue'),
        meta: { title: 'Team sign-in', permissions: ['attendance.checkin'] },
      },
      {
        path: 'attendance/records',
        name: 'attendance-records',
        component: () => import('@/features/attendance/views/AttendanceRecordsView.vue'),
        meta: {
          title: 'Attendance records',
          permissions: ['attendance.view', 'attendance.device.manage'],
        },
      },
      {
        path: 'attendance/setup',
        name: 'attendance-setup',
        component: () => import('@/features/attendance/views/AttendancePoliciesView.vue'),
        meta: {
          title: 'Attendance setup',
          permissions: ['attendance.policy.view', 'attendance.location.view'],
        },
      },
      {
        path: 'customers',
        name: 'customers',
        component: () => import('@/features/customers/views/CustomersView.vue'),
        meta: { title: 'Customers', permissions: ['customer.view'] },
      },
      {
        path: 'customers/:id',
        name: 'customer',
        component: () => import('@/features/customers/views/CustomerDetailView.vue'),
        meta: { title: 'Customer', permissions: ['customer.view'] },
      },
      {
        path: 'leads',
        name: 'leads',
        component: () => import('@/features/sales/views/LeadsView.vue'),
        meta: { title: 'Leads', permissions: ['lead.view'] },
      },
      {
        path: 'sales',
        name: 'sales',
        component: () => import('@/features/sales/views/SalesView.vue'),
        meta: { title: 'Sales', permissions: ['sale.view'] },
      },
      {
        path: 'sales/:id',
        name: 'sale',
        component: () => import('@/features/sales/views/SaleDetailView.vue'),
        meta: { title: 'Sale', permissions: ['sale.view'] },
      },
      {
        path: 'tickets',
        name: 'tickets',
        component: () => import('@/features/tickets/views/TicketsView.vue'),
        meta: { title: 'Tickets', permissions: ['ticket.queue.view'], teamLead: true },
      },
      {
        path: 'tickets/mine',
        name: 'my-tickets',
        component: () => import('@/features/tickets/views/MyTicketsView.vue'),
        meta: { title: 'My tickets', permissions: ['ticket.work.start'] },
      },
      {
        path: 'tickets/:id',
        name: 'ticket',
        component: () => import('@/features/tickets/views/TicketDetailView.vue'),
        meta: { title: 'Ticket', permissions: ['ticket.view', 'ticket.work.start'] },
      },
      {
        path: 'dispatch',
        name: 'dispatch',
        component: () => import('@/features/tickets/views/DispatchView.vue'),
        meta: { title: 'Dispatch board', permissions: ['ticket.assign'] },
      },
      {
        path: 'inventory',
        name: 'inventory',
        component: () => import('@/features/inventory/views/InventoryView.vue'),
        meta: {
          title: 'Stock',
          permissions: ['inventory.item.view'],
          requires: ['stock_location.view'],
        },
      },
      {
        path: 'inventory/receive',
        name: 'inventory-receive',
        component: () => import('@/features/inventory/views/ReceiveStockView.vue'),
        meta: { title: 'Receive a delivery', permissions: ['inventory.receive'] },
      },
      {
        path: 'inventory/issue',
        name: 'inventory-issue',
        component: () => import('@/features/inventory/views/IssueStockView.vue'),
        meta: { title: 'Issue or move stock', permissions: ['inventory.movement.create'] },
      },
      {
        path: 'inventory/counts/:id',
        name: 'stock-count',
        component: () => import('@/features/inventory/views/StockCountView.vue'),
        meta: { title: 'Stock count', permissions: ['inventory.count', 'inventory.item.view'] },
      },
      {
        path: 'scan',
        name: 'scan',
        component: () => import('@/features/inventory/views/ScanView.vue'),
        meta: { title: 'Scan a code', permissions: ['inventory.scan'] },
      },
      {
        path: 'denied',
        name: 'forbidden',
        component: () => import('@/features/dashboard/views/AccessDeniedView.vue'),
        meta: { title: 'No access' },
      },
      {
        path: 'inventory/mine',
        name: 'my-stock',
        component: () => import('@/features/inventory/views/MyStockView.vue'),
        meta: { title: 'My stock', permissions: ['ticket.work.start'] },
      },
      {
        path: 'retention',
        name: 'retention',
        component: () => import('@/features/retention/views/RetentionView.vue'),
        meta: { title: 'Retention queue', permissions: ['retention.view'] },
      },
      {
        path: 'settings/retention',
        name: 'retention-settings',
        component: () => import('@/features/retention/views/RetentionSettingsView.vue'),
        meta: {
          title: 'Retention settings',
          permissions: ['tenant.settings.manage'],
          requires: ['retention.view'],
        },
      },
      {
        path: 'incidents',
        name: 'incidents',
        component: () => import('@/features/incidents/views/IncidentsView.vue'),
        meta: { title: 'Incidents', permissions: ['incident.view'] },
      },
      {
        path: 'incidents/:id',
        name: 'incident',
        component: () => import('@/features/incidents/views/IncidentDetailView.vue'),
        meta: { title: 'Incident', permissions: ['incident.view'] },
      },
      {
        path: 'infrastructure',
        name: 'infrastructure',
        component: () => import('@/features/infrastructure/views/InfrastructureView.vue'),
        meta: { title: 'Infrastructure', permissions: ['infrastructure.view'] },
      },
      {
        path: 'network',
        name: 'network',
        component: () => import('@/features/infrastructure/views/NetworkView.vue'),
        meta: {
          title: 'Network map',
          permissions: ['network.view'],
          requires: ['infrastructure.view'],
        },
      },
      {
        path: 'settings/network',
        name: 'network-settings',
        component: () => import('@/features/infrastructure/views/NetworkSettingsView.vue'),
        meta: {
          title: 'Network settings',
          permissions: ['network.edit'],
          requires: ['network.view'],
        },
      },
      {
        path: 'infrastructure/:id',
        name: 'infrastructure-asset',
        component: () => import('@/features/infrastructure/views/InfrastructureDetailView.vue'),
        meta: { title: 'Infrastructure asset', permissions: ['infrastructure.view'] },
      },
      {
        path: 'settings/tickets',
        name: 'ticket-settings',
        component: () => import('@/features/tickets/views/TicketSettingsView.vue'),
        meta: {
          title: 'Ticket settings',
          permissions: ['tenant.settings.manage'],
          requires: ['ticket.view'],
        },
      },
      {
        path: 'billing/linking',
        name: 'billing-linking',
        component: () => import('@/features/customers/views/BillingLinkQueueView.vue'),
        meta: { title: 'Billing link queue', permissions: ['billing_account.link'] },
      },
      {
        path: 'settings/billing',
        name: 'billing-settings',
        component: () => import('@/features/integrations/views/BillingIntegrationView.vue'),
        meta: { title: 'Billing integration', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'settings/stock',
        name: 'inventory-settings',
        component: () => import('@/features/inventory/views/InventorySettingsView.vue'),
        meta: {
          title: 'Stock settings',
          permissions: ['tenant.settings.manage'],
          requires: ['inventory.receipt.view'],
        },
      },
      {
        path: 'settings/sales',
        name: 'sales-settings',
        component: () => import('@/features/sales/views/SalesSettingsView.vue'),
        meta: {
          title: 'Sales settings',
          permissions: ['tenant.settings.manage'],
          requires: ['sale.view', 'lead.view'],
        },
      },
      {
        path: 'settings/health',
        name: 'workspace-health',
        component: () => import('@/features/organization/views/TenantHealthView.vue'),
        meta: { title: 'Workspace health', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'audit',
        name: 'audit',
        component: () => import('@/features/audit/views/AuditTrailView.vue'),
        meta: { title: 'Audit trail', permissions: ['audit.view'] },
      },
      {
        path: 'settings',
        name: 'settings-hub',
        component: () => import('@/features/organization/views/SettingsHubView.vue'),
        meta: { title: 'Settings' },
      },
      {
        path: 'settings/import',
        name: 'data-import',
        component: () => import('@/features/imports/views/DataImportView.vue'),
        meta: { title: 'Data import', permissions: ['data.import'] },
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/features/rbac/views/RolesView.vue'),
        meta: { title: 'Roles & permissions', permissions: ['role.view'] },
      },
      {
        path: 'people',
        name: 'people',
        component: () => import('@/features/rbac/views/PeopleView.vue'),
        meta: { title: 'People', permissions: ['role.view'] },
      },
      {
        path: 'settings/workspace',
        name: 'workspace-settings',
        component: () => import('@/features/organization/views/WorkspaceSettingsView.vue'),
        meta: { title: 'Workspace settings', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/features/notifications/views/NotificationsView.vue'),
        meta: { title: 'Notifications' },
      },
      {
        path: 'settings/notifications',
        name: 'notification-preferences',
        component: () => import('@/features/notifications/views/NotificationPreferencesView.vue'),
        meta: { title: 'Notification preferences' },
      },
      {
        path: 'settings/mail',
        name: 'tenant-mail-server',
        component: () => import('@/features/notifications/views/TenantMailServerView.vue'),
        meta: { title: 'Email server', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'settings/notification-rules',
        name: 'notification-rules',
        component: () => import('@/features/notifications/views/NotificationChannelsView.vue'),
        meta: { title: 'Notifications', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'performance',
        name: 'performance',
        component: () => import('@/features/performance/views/PerformanceView.vue'),
        meta: { title: 'Performance', permissions: ['performance.view'] },
      },
      {
        path: 'settings/performance',
        name: 'performance-targets',
        component: () => import('@/features/performance/views/PerformanceTargetsView.vue'),
        meta: {
          title: 'Ticket targets',
          permissions: ['performance.target.manage'],
          requires: ['performance.view'],
        },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/features/reports/views/ReportsView.vue'),
        meta: { title: 'Reports', permissions: ['report.view'] },
      },
      {
        path: 'reports/:code',
        name: 'report',
        component: () => import('@/features/reports/views/ReportView.vue'),
        meta: { title: 'Report', permissions: ['report.view'] },
      },
      {
        path: 'settings/subscription',
        name: 'subscription-billing',
        component: () => import('@/features/subscription/views/SubscriptionView.vue'),
        meta: { title: 'Subscription & billing', permissions: ['tenant.settings.manage'] },
      },
      {
        path: 'settings/security',
        name: 'account-security',
        component: () => import('@/features/auth/views/AccountSecurityView.vue'),
        meta: { title: 'Account security' },
      },
    ],
  },
  {
    path: '/platform',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { auth: true, platform: true },
    children: [
      {
        path: '',
        name: 'platform-dashboard',
        component: () => import('@/features/platform/views/PlatformDashboardView.vue'),
        meta: { title: 'Platform' },
      },
      {
        path: 'denied',
        name: 'platform-forbidden',
        component: () => import('@/features/dashboard/views/AccessDeniedView.vue'),
        meta: { title: 'No access' },
      },
      {
        path: 'tenants',
        name: 'platform-tenants',
        component: () => import('@/features/platform/views/TenantsView.vue'),
        meta: { title: 'Tenants', platformPermissions: ['platform.tenant.view'] },
      },
      {
        path: 'tenants/:id',
        name: 'platform-tenant',
        component: () => import('@/features/platform/views/TenantDetailView.vue'),
        meta: { title: 'Tenant', platformPermissions: ['platform.tenant.view'] },
      },
      {
        path: 'notifications',
        name: 'platform-notifications',
        component: () => import('@/features/notifications/views/NotificationsView.vue'),
        meta: { title: 'Notifications' },
      },
      {
        path: 'billing',
        name: 'platform-billing',
        component: () => import('@/features/platform/views/BillingView.vue'),
        meta: {
          title: 'Billing',
          platformPermissions: ['platform.subscription.view', 'platform.invoice.view'],
        },
      },
      {
        path: 'plans',
        name: 'platform-plans',
        component: () => import('@/features/platform/views/PlansView.vue'),
        meta: { title: 'Plans', platformPermissions: ['platform.subscription.view'] },
      },
      {
        path: 'templates',
        name: 'platform-templates',
        component: () => import('@/features/platform/views/TemplatesView.vue'),
        meta: { title: 'Templates', platformPermissions: ['platform.payment.settings'] },
      },
      {
        path: 'settings/mail',
        name: 'platform-mail',
        component: () => import('@/features/platform/views/MailServerView.vue'),
        meta: { title: 'Mail server', platformPermissions: ['platform.settings.manage'] },
      },
      {
        path: 'settings/security',
        name: 'platform-account-security',
        component: () => import('@/features/auth/views/AccountSecurityView.vue'),
        meta: { title: 'Account security' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/dashboard/views/NotFoundView.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

installGuards(router)

// A deploy between page load and navigation leaves stale chunk URLs behind; reload
// once onto the new build instead of showing a broken screen (SDD §10.5).
router.onError((error, to) => {
  const stale = /Failed to fetch dynamically imported module|Importing a module script failed/i
  if (stale.test(String(error)) && !sessionStorage.getItem('fumbo.reloaded')) {
    sessionStorage.setItem('fumbo.reloaded', '1')
    window.location.assign(to.fullPath)
  }
})
router.afterEach(() => sessionStorage.removeItem('fumbo.reloaded'))
